import React, { useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../services/api';
import { renderReact18 } from 'molstar/lib/mol-plugin-ui/react18';
import { createPluginUI } from 'molstar/lib/mol-plugin-ui';
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { PluginConfig } from 'molstar/lib/mol-plugin/config';
import { DefaultPluginSpec } from './spec';
import { ColorNames } from 'molstar/lib/mol-util/color/names';
import "molstar/lib/mol-plugin-ui/skin/light.scss";
// import "./skin/dark.scss";


const MolstarViewer = ({ pdbUrl, pdbFile }) => {
  const viewerRef = useRef(null);
  const pluginInstanceRef = useRef(null);

  const initMolstar = useMemo(() => async () => {
    const MySpec = {
      ...DefaultPluginSpec(),
      layout: {
        initial: {
          isExpanded: false,
          showControls: false,
          showRemoteState: false,
          showSequence: true,
          showLog: false,
          showLeftPanel: true,

          //controlsDisplay: "reactive",
        },
      },
      config: [
        [PluginConfig.Viewport.ShowExpand, true],
        [PluginConfig.Viewport.ShowControls, true],
        [PluginConfig.Viewport.ShowSettings, true],
        [PluginConfig.Viewport.ShowSelectionMode, true],
        [PluginConfig.Viewport.ShowAnimation, false],
        [PluginConfig.Viewport.ShowTrajectoryControls, false],
      ]
    }

    // Dispose of any existing plugin instance first
    if (pluginInstanceRef.current) {
      pluginInstanceRef.current.dispose();
      pluginInstanceRef.current = null;
    }

    if (viewerRef.current) {
      const plugin = await createPluginUI({
        target: viewerRef.current,
        spec: MySpec,
        render: renderReact18,
      });

      const renderer = plugin.canvas3d.props.renderer;
      PluginCommands.Canvas3D.SetSettings(plugin, {
        settings: {
          renderer: {
            ...renderer,
            backgroundColor: ColorNames.black,
          },
        },
      });

      pluginInstanceRef.current = plugin;
    }

    if (pluginInstanceRef.current && (pdbUrl || pdbFile)) {
      try {
        let pdbData = '';
        
        if (pdbFile) {
          // Read file content directly
          console.log('MolstarViewer: Loading PDB from file:', pdbFile.name);
          pdbData = await pdbFile.text();
        } else if (pdbUrl) {
          // Download PDB data with authentication using axios
          console.log('MolstarViewer: Loading PDB from URL:', pdbUrl);
          const response = await axios.get(pdbUrl, {
            responseType: 'text',
            headers: getAuthHeaders()
          });
          pdbData = response.data;
        }
        
        console.log('MolstarViewer: PDB data received, length:', pdbData.length);
        
        // Clear existing structures
        await pluginInstanceRef.current.clear();
        console.log('MolstarViewer: Cleared existing structures');
        
        // Load PDB data from string instead of URL
        const data = await pluginInstanceRef.current.builders.data.rawData(
          { data: pdbData, label: pdbFile ? pdbFile.name : 'PDB Structure' },
          { state: { isGhost: true } }
        );
        console.log('MolstarViewer: Raw data loaded');
        
        const trajectory = await pluginInstanceRef.current.builders.structure.parseTrajectory(data, 'pdb');
        console.log('MolstarViewer: Trajectory parsed');

        const model = await pluginInstanceRef.current.builders.structure.createModel(trajectory);
        console.log('MolstarViewer: Model created');
        
        const structure = await pluginInstanceRef.current.builders.structure.createStructure(
          model, { name: 'model', params: {} });
        console.log('MolstarViewer: Structure created');

        const polymer = await pluginInstanceRef.current.builders.structure.tryCreateComponentStatic(
          structure, 'polymer');
        if (polymer) {
          await pluginInstanceRef.current.builders.structure.representation.addRepresentation(
            polymer, {
              type: 'line',
              colorTheme: 'element-symbol',
              size: 'uniform',
              sizeParams: { value: 0.1 }
            }
          );
          console.log('MolstarViewer: Polymer representation added');
        }

        const ligand = await pluginInstanceRef.current.builders.structure.tryCreateComponentStatic(
          structure, 'ligand');
        if (ligand) {
          await pluginInstanceRef.current.builders.structure.representation.addRepresentation(
            ligand, {
              type: 'ball-and-stick'
            }
          );
          console.log('MolstarViewer: Ligand representation added');
        }
        
        console.log('MolstarViewer: Structure loading completed successfully');
      } catch (error) {
        console.error('MolstarViewer: Failed to load the PDB structure:', error);
        console.error('MolstarViewer: Error details:', error.response?.data || error.message);
      }
    } else {
      console.log('MolstarViewer: No plugin instance or pdbUrl/pdbFile', {
        hasPlugin: !!pluginInstanceRef.current,
        pdbUrl: pdbUrl,
        pdbFile: pdbFile
      });
    }
  }, [pdbUrl, pdbFile]);

  useEffect(() => {
    initMolstar();

    return () => {
      if (pluginInstanceRef.current) {
        pluginInstanceRef.current.dispose();
        pluginInstanceRef.current = null;
      }
    };
  }, [initMolstar]);

  return <div ref={viewerRef} style={{
    position: 'relative', width: '100%', height: '100%', zIndex: 1300 }} />;
};

export default MolstarViewer;